import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Download, Clapperboard, Layers, ChevronDown, ChevronRight } from "lucide-react";
import { api, authedUrl } from "@/lib/api";
import { fmtDate, fmtDuration } from "@/lib/format";
import { VerdictBadge } from "@/components/RunReview";
const MODE_NAME_SHORT = { fast: "Fast", hq: "HQ", melab: "M&E Lab", gaudio: "Gaudio" };

const STATUS = { complete: ["Complete", "text-emerald-400 border-emerald-800 bg-emerald-400/10"], processing: ["Processing", "text-sky-300 border-sky-800 bg-sky-400/10"],
  starting: ["Starting GPU", "text-amber-300 border-amber-800 bg-amber-400/10"], failed: ["Failed", "text-rose-300 border-rose-800 bg-rose-500/10"] };
const MODE_NAME = { fast: "Fast", hq: "HQ", melab: "M&E Lab", gaudio: "Gaudio", audioshake: "AudioShake" };
const MODES = (m) => m.map((x) => MODE_NAME[x] || x).join(" + ");

// consecutive episodes of the same batch collapse under one header row (list is newest-first)
const groupRows = (rows) => {
  const out = [];
  for (const r of rows) {
    const last = out[out.length - 1];
    if (r.batch && last?.batch?.id === r.batch.id) last.items.push(r);
    else out.push(r.batch ? { batch: r.batch, items: [r] } : { items: [r] });
  }
  out.forEach((g) => g.batch && g.items.sort((a, b) => (a.batch.index || 0) - (b.batch.index || 0)));
  return out;
};

const BatchHeader = ({ g, open, onToggle }) => {
  const done = g.items.filter((r) => r.overall === "complete").length;
  const failed = g.items.filter((r) => r.overall === "failed").length;
  const outputs = g.items.reduce((a, r) => a + r.runs.reduce((b, x) => b + x.outputs, 0), 0);
  return (
    <tr data-testid={`batch-header-${g.batch.id}`} onClick={onToggle} className="bg-[#111722] border-b border-[#1c2433] cursor-pointer hover:bg-[#161e2c]">
      <td className="px-4 py-2.5" colSpan={4}>
        <div className="flex items-center gap-2 text-sm"><span className="text-slate-500">{open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</span><Layers size={14} className="text-cyan-300" />
          <span className="font-medium text-slate-100">Batch · {fmtDate(g.items[0].created_at)}</span><span className="text-xs text-slate-500">· {g.items.length} episode{g.items.length > 1 ? "s" : ""} · {MODES(g.batch.modes || [])} · one GPU worker</span></div>
      </td>
      <td className="px-4 py-2.5">
        <div className="flex items-center gap-2"><div className="h-1.5 w-28 rounded-full bg-[#1E2738] overflow-hidden"><div className={`h-full ${failed ? "bg-rose-400" : "bg-emerald-400"}`} style={{ width: `${(done / g.items.length) * 100}%` }} /></div>
          <span data-testid={`batch-progress-${g.batch.id}`} className="font-mono text-[11px] text-slate-300">{done}/{g.items.length}{failed ? ` · ${failed} failed` : ""}</span></div>
      </td>
      <td className="px-4 py-2.5 font-mono text-xs text-slate-400">{outputs} WAVs</td>
      <td className="px-4 py-2.5 font-mono text-[11px] text-slate-400">{g.items.filter((r) => r.review?.reviewed > 0).length}/{g.items.length} reviewed</td>
      <td />
    </tr>
  );
};

const EpisodeVerdict = ({ r }) => {
  const rv = r.review || { verdict: "unreviewed", reviewed: 0, completed: 0 };
  if (!rv.completed) return <span data-testid={`episode-verdict-${r.id}`} className="text-[11px] text-slate-600">—</span>;
  return (
    <div className="flex flex-col items-start gap-0.5">
      <VerdictBadge verdict={rv.verdict} testId={`episode-verdict-${r.id}`} />
      <span data-testid={`episode-reviewed-count-${r.id}`} className="text-[10px] text-slate-500 font-mono">{rv.reviewed}/{rv.completed} run{rv.completed === 1 ? "" : "s"} reviewed{rv.best_run_mode && rv.reviewed ? ` · best: ${MODE_NAME_SHORT[rv.best_run_mode] || rv.best_run_mode}` : ""}</span>
    </div>
  );
};

export default function Episodes() {
  const nav = useNavigate();
  const [rows, setRows] = useState(null);
  const [collapsed, setCollapsed] = useState({});
  useEffect(() => {
    const load = () => api.get("/episodes").then((r) => setRows(r.data)).catch(() => setRows([]));
    load();
    const t = setInterval(load, 10000);
    return () => clearInterval(t);
  }, []);
  return (
    <div data-testid="episodes-page" className="max-w-[1200px] mx-auto px-4 md:px-6 py-8 space-y-6">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-3xl md:text-4xl font-bold tracking-tight">Episodes</h1>
          <p className="text-sm text-slate-400 mt-1">Upload the episode MP4 (or WAV), choose Fast / HQ / Both, run it on the GPU, download DX · MX · FX · ME stems for FCP.</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="new-batch-button" onClick={() => nav("/episodes/batch")} className="flex items-center gap-2 px-4 h-11 rounded-sm border border-[#3A4A68] hover:bg-[#1E2738] text-sm font-semibold">
            <Layers size={16} /> Batch
          </button>
          <button data-testid="new-episode-button" onClick={() => nav("/episodes/new")} className="flex items-center gap-2 px-5 h-11 rounded-sm bg-[#E11D48] hover:bg-[#be123c] text-white text-sm font-semibold">
            <Plus size={16} /> New Episode Separation
          </button>
        </div>
      </div>
      <div className="panel overflow-hidden">
        {rows === null ? <div className="p-8 text-sm text-slate-500">Loading…</div> : rows.length === 0 ? (
          <div data-testid="episodes-empty" className="p-12 text-center space-y-3">
            <Clapperboard size={28} className="mx-auto text-slate-600" />
            <div className="text-slate-300">No episodes yet.</div>
            <div className="text-xs text-slate-500">Click <b>New Episode Separation</b>, upload the episode audio (WAV / AIFF / FLAC / MP3 / AAC — or an MP4/MOV whose audio track we'll extract), choose Fast, HQ or Both, and start.</div>
          </div>
        ) : (
          <table data-testid="episodes-table" className="w-full text-sm">
            <thead className="text-left micro border-b border-[#1c2433]"><tr>
              <th className="px-4 py-3">Episode</th><th className="px-4 py-3">Date</th><th className="px-4 py-3">Duration</th><th className="px-4 py-3">Mode</th><th className="px-4 py-3">Status</th><th className="px-4 py-3">Outputs</th><th className="px-4 py-3">Verdict</th><th className="px-4 py-3 text-right">Download</th>
            </tr></thead>
            <tbody>
              {groupRows(rows).flatMap((g) => {
                const open = g.batch ? !collapsed[g.batch.id] : true;
                const header = g.batch ? [<BatchHeader key={`h-${g.batch.id}`} g={g} open={open} onToggle={() => setCollapsed((c) => ({ ...c, [g.batch.id]: !c[g.batch.id] }))} />] : [];
                if (!open) return header;
                return header.concat(g.items.map((r) => {
                const [label, cls] = STATUS[r.overall] || [r.overall, "text-slate-300 border-[#3A4A68]"];
                const outputs = r.runs.reduce((a, x) => a + x.outputs, 0);
                return (
                  <tr key={r.id} data-testid={`episode-row-${r.id}`} onClick={() => nav(`/episodes/${r.id}`)} className={`border-b border-[#1c2433] hover:bg-[#1A2130] cursor-pointer ${g.batch ? "bg-[#0d121b]" : ""}`}>
                    <td className={`px-4 py-3 ${g.batch ? "pl-10" : ""}`}><div className="font-medium text-slate-100">{r.name}</div><div className="text-[11px] text-slate-500 font-mono truncate max-w-[320px]">{r.source_filename}</div></td>
                    <td className="px-4 py-3 text-slate-400 text-xs">{fmtDate(r.created_at)}</td>
                    <td className="px-4 py-3 font-mono text-xs">{fmtDuration(r.duration)}</td>
                    <td className="px-4 py-3 text-xs">{MODES(r.modes)}</td>
                    <td className="px-4 py-3"><span data-testid={`episode-status-${r.id}`} className={`font-mono text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border ${cls}`}>{label}</span></td>
                    <td className="px-4 py-3 font-mono text-xs">{outputs} WAV{outputs === 1 ? "" : "s"}</td>
                    <td className="px-4 py-3"><EpisodeVerdict r={r} /></td>
                    <td className="px-4 py-3 text-right">
                      {outputs > 0 && <a data-testid={`episode-download-${r.id}`} href={authedUrl(`/benchmark/${r.id}/download-all`)} onClick={(e) => e.stopPropagation()}
                        className="inline-flex items-center gap-1 px-2.5 py-1 rounded-sm border border-[#253044] hover:bg-[#1E2738] text-xs"><Download size={11} /> All WAVs</a>}
                    </td>
                  </tr>
                );
                }));
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
